import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { PlayCircle, CheckCircle2, Plus, Minus, PawPrint, X, Images } from "lucide-react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import Ticker from "../components/Ticker";
import FloatingCircles from "../components/FloatingCircles";
import GlassCard from "../components/GlassCard";
import SectionHeader from "../components/SectionHeader";
import { LOGO_URL, api, fileUrl } from "../lib/kk";

const GIRLS = [
  { id: "luna", name: "Latto", detail: "Cream Cocoa", tag: "Dam", img: "https://customer-assets.emergentagent.com/job_premium-frenchies/artifacts/j6ouaxmo_IMG_8296.JPG", tint: "from-[#c9a87c]/30" },
  { id: "lily", name: "Lily", detail: "Pied", tag: "Dam", img: "https://customer-assets.emergentagent.com/job_premium-frenchies/artifacts/nwpgy4tt_IMG_2558.JPG", tint: "from-[#9aa0a6]/30" },
  { id: "reina", name: "Reina", detail: "Blue & Tan Tri", tag: "Dam", img: "https://customer-assets.emergentagent.com/job_premium-frenchies/artifacts/9c023se4_IMG_8658.JPG", tint: "from-[#6b5a3e]/35" },
  { id: "luna2", name: "Luna", detail: "Blue & Tan", tag: "Dam", img: "https://customer-assets.emergentagent.com/job_premium-frenchies/artifacts/vpko7rei_IMG_2488.JPEG", tint: "from-[#3a3320]/40" },
];

const STUDS = [
  { id: "ace", name: "Ace", detail: "Fluffy Black & Tan Tri", tag: "Proven", img: "https://customer-assets.emergentagent.com/job_premium-frenchies/artifacts/rn3xvtoo_IMG_3689.JPG", tint: "from-[#2a2418]/45" },
];

const AVAILABLE = [
  { id: "lilmama", name: "Lil Mama", detail: "Blue & Tan", tag: "Available", img: "https://customer-assets.emergentagent.com/job_premium-frenchies/artifacts/wxafj5p4_IMG_4626.JPG", tint: "from-[#5a4a3a]/40" },
];

const COLORS = ["Blue", "Cocoa", "Cream", "Tan Points", "Fluffy Carrier", "Testable Chocolate"];

const HEALTH = [
  "AKC Registered", "OFA Cardiac — Normal", "OFA Patellas — Normal", "OFA Trachea — Normal",
  "4-Panel DNA — Clear", "Cystinuria 3 — Clear", "DM — Clear", "HUU — Clear",
];

const FAQS = [
  { q: "How does your stud service work?", a: "We offer chilled semen shipped overnight anywhere in the U.S. Each shipment includes a semen analysis prepared by our veterinarian." },
  { q: "What is included in the stud fee?", a: "The stud fee covers one breeding attempt, semen collection, evaluation, extension, and preparation. Shipping is additional." },
  { q: "Do you offer a breeding guarantee?", a: "Yes. If the female does not conceive, we offer a free repeat breeding on the next heat cycle." },
  { q: "What do I need to provide?", a: "Health testing results, current photos of your female, and proof of AKC registration." },
  { q: "How do I join the puppy waitlist?", a: "Fill out the waitlist form. A deposit secures your spot." },
  { q: "How will puppies be delivered?", a: "We can deliver by car, delivery service, or flight, subject to an extra cost depending on travel distance." },
];

const GalleryModal = ({ dog, photos, onClose }) => {
  const imgs = photos.length ? photos : [dog.img];
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose} data-testid="gallery-modal">
      <div className="kk-glass max-w-3xl w-full max-h-[88vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="kk-heading text-2xl">{dog.name}</h3>
            <p className="text-[#999] text-sm">{dog.detail} · {dog.tag}</p>
          </div>
          <button onClick={onClose} className="text-[#A8A9AD] hover:text-[#BB0000]" data-testid="gallery-close"><X size={26} /></button>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          {imgs.map((src, i) => (
            <img key={i} src={src} alt={`${dog.name} ${i + 1}`} className="w-full sm:w-[300px] aspect-[3/4] object-cover object-center bg-black/30 rounded-xl border border-white/10 shadow-lg" style={{ imageOrientation: "from-image" }} />
          ))}
        </div>
        {!photos.length && (
          <p className="text-[#777] text-xs text-center mt-4">More photos coming soon — upload them from the Admin panel.</p>
        )}
      </div>
    </div>
  );
};

const DogCard = ({ dog, galleries, onOpen }) => {
  const photos = (galleries[dog.id] || []).map(fileUrl);
  const cover = photos[0] || dog.img;
  return (
    <GlassCard className="overflow-hidden cursor-pointer w-full sm:w-[340px]" data-testid={`dog-card-${dog.id}`} onClick={() => onOpen(dog, photos)}>
      <div className="relative aspect-[3/4] overflow-hidden">
        <img src={cover} alt={dog.name} className="w-full h-full object-cover object-center" style={{ imageOrientation: "from-image" }} />
        <div className={`absolute inset-0 bg-gradient-to-t ${dog.tint} to-transparent`} />
        <span className="absolute top-3 left-3 inline-flex items-center gap-1 bg-black/60 text-[#A8A9AD] text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full">
          <Images size={11} /> {photos.length || "Gallery"}
        </span>
        <span className="absolute top-3 right-3 bg-[#BB0000] text-white text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full">
          {dog.tag}
        </span>
      </div>
      <div className="p-5">
        <h4 className="kk-heading text-xl">{dog.name}</h4>
        <p className="text-[#999999] text-sm mt-1">{dog.detail}</p>
        <p className="text-[#BB0000] text-xs font-bold uppercase tracking-wider mt-3 inline-flex items-center gap-1"><PawPrint size={12} /> View Gallery</p>
      </div>
    </GlassCard>
  );
};

const SubHeading = ({ children }) => (
  <h3 className="kk-heading text-2xl border-l-[3px] border-[#BB0000] pl-4 mb-8">{children}</h3>
);

const FaqItem = ({ q, a, open, onToggle, idx }) => (
  <div
    className="kk-glass overflow-hidden transition-colors"
    style={{ background: open ? "rgba(187,0,0,0.08)" : undefined, borderColor: open ? "rgba(187,0,0,0.35)" : undefined }}
    data-testid={`faq-item-${idx}`}
  >
    <button onClick={onToggle} className="w-full flex items-center justify-between gap-4 text-left p-5" data-testid={`faq-toggle-${idx}`}>
      <span className="kk-heading text-base sm:text-lg" style={{ textTransform: "none", letterSpacing: "normal" }}>{q}</span>
      <span className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors" style={{ background: open ? "#BB0000" : "#3a3a3a", color: "#fff" }}>
        {open ? <Minus size={18} /> : <Plus size={18} />}
      </span>
    </button>
    {open && <p className="px-5 pb-5 text-[#E0E0E0] text-sm leading-relaxed">{a}</p>}
  </div>
);

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      setForm({ name: "", email: "", subject: "", message: "" });
      toast.success("Message sent!");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <GlassCard hover={false} className="p-8">
      <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
        <input className="kk-input" placeholder="Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
        <input className="kk-input" type="email" placeholder="Email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
        <input className="kk-input" placeholder="Subject" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="contact-subject" />
        <textarea className="kk-input min-h-[130px] resize-none" placeholder="Message" required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
        <button type="submit" disabled={loading} className="kk-btn kk-btn-primary w-full rounded-full py-4 disabled:opacity-60" data-testid="contact-submit">
          {loading ? "Sending…" : "Send Message"}
        </button>
        {sent && <p className="text-[#4CAF50] text-center font-semibold" data-testid="contact-success">Message sent!</p>}
      </form>
    </GlassCard>
  );
};

export default function Home() {
  const location = useLocation();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(0);
  const [galleries, setGalleries] = useState({});
  const [videoPath, setVideoPath] = useState(null);
  const [activeDog, setActiveDog] = useState(null);

  useEffect(() => {
    api.get("/content").then(({ data }) => {
      setGalleries(data.galleries || {});
      setVideoPath(data.video_path || null);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (location.state?.scrollTo) {
      const id = location.state.scrollTo;
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, [location.state]);

  const openGallery = (dog, photos) => setActiveDog({ dog, photos });

  return (
    <Layout>
      {activeDog && <GalleryModal dog={activeDog.dog} photos={activeDog.photos} onClose={() => setActiveDog(null)} />}

      {/* HERO */}
      <section className="relative overflow-hidden py-16 md:py-24" data-testid="hero">
        <FloatingCircles />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <img src={LOGO_URL} alt="Kallie's Kennel" className="mx-auto h-[240px] w-auto object-contain kk-reveal drop-shadow-[0_0_45px_rgba(187,0,0,0.45)]" />
          <h1 className="kk-heading text-4xl sm:text-5xl mt-14 leading-tight" style={{ fontSize: "clamp(2rem, 5vw, 2.8rem)" }}>
            Premium French Bulldogs,<br /><span className="text-[#BB0000]">raised with heart</span>
          </h1>
          <p className="text-[#999999] text-base sm:text-lg max-w-2xl mx-auto mt-5">
            Quality genetics, comprehensive health testing, and a family-first approach to breeding exceptional Frenchies.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-9">
            <button onClick={() => document.getElementById("puppies")?.scrollIntoView({ behavior: "smooth" })} className="kk-btn kk-btn-primary rounded-full px-8 py-4" data-testid="hero-view-frenchies">
              View Our Frenchies
            </button>
            <button onClick={() => navigate("/puppy-waitlist")} className="kk-btn kk-btn-primary rounded-full px-8 py-4" data-testid="hero-reserve-puppy">
              Reserve a Puppy
            </button>
            <button onClick={() => navigate("/puppy-waitlist")} className="kk-btn kk-btn-outline rounded-full px-8 py-4" data-testid="hero-join-waitlist">
              Join Waitlist
            </button>
          </div>
        </div>
      </section>

      <Ticker />

      {/* ABOUT */}
      <section id="about" className="py-24 md:py-28 px-6">
        <div className="max-w-[900px] mx-auto text-center">
          <p className="kk-label mb-3">About Kallie's Kennel</p>
          <h2 className="kk-heading text-3xl sm:text-4xl lg:text-5xl mb-6">Our Story</h2>
          <p className="text-[#E0E0E0] text-base sm:text-lg leading-relaxed">
            Based in Columbus, Ohio, Kallie's Kennel is dedicated to producing healthy, well-tempered French Bulldogs with exceptional structure and rare color genetics. Every dog in our program is AKC registered, fully health tested, and raised with love. Quality, health, temperament, and family — that's our promise.
          </p>
        </div>
      </section>

      {/* PUPPIES */}
      <section id="puppies" className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <SectionHeader label="Our Frenchies" title="Raised with love," highlight="built for excellence" stack />

          <div className="mb-16">
            <SubHeading>Available Puppies</SubHeading>
            <div className="flex flex-wrap justify-center gap-6">
              {AVAILABLE.map((d) => <DogCard key={d.id} dog={d} galleries={galleries} onOpen={openGallery} />)}
            </div>
          </div>

          {/* UPCOMING LITTER */}
          <div id="upcoming" className="mb-16">
            <SubHeading>Our Next Upcoming Litter</SubHeading>
            {(() => {
              const litter = (galleries["upcoming"] || []).map(fileUrl);
              return litter.length ? (
                <div className="flex flex-wrap justify-center gap-4" data-testid="upcoming-gallery">
                  {litter.map((src, i) => (
                    <button
                      key={i}
                      onClick={() => setActiveDog({ dog: { name: "Upcoming Litter", detail: "Coming Soon", tag: "Waitlist Open" }, photos: litter })}
                      className="kk-glass kk-glass-hover overflow-hidden p-0 w-[46%] sm:w-[220px]"
                      data-testid={`upcoming-photo-${i}`}
                    >
                      <img src={src} alt={`Upcoming litter ${i + 1}`} className="w-full aspect-[3/4] object-cover object-center" style={{ imageOrientation: "from-image" }} />
                    </button>
                  ))}
                </div>
              ) : (
                <GlassCard hover={false} className="p-12 text-center max-w-2xl mx-auto" data-testid="upcoming-empty">
                  <PawPrint size={34} className="mx-auto text-[#BB0000] mb-4" />
                  <p className="text-[#E0E0E0] leading-relaxed">
                    A brand-new litter is on the way! Photos of the parents and puppies will appear here soon. Reserve your spot now — deposits secure your place in line.
                  </p>
                </GlassCard>
              );
            })()}
            <div className="text-center mt-8">
              <button
                onClick={() => navigate("/puppy-waitlist")}
                className="kk-btn kk-btn-primary rounded-full px-10 py-4"
                data-testid="upcoming-waitlist-btn"
              >
                Click to Join the Waiting List
              </button>
            </div>
          </div>

          <div className="mb-16">
            <SubHeading>Our Girls</SubHeading>
            <div className="flex flex-wrap justify-center gap-6">
              {GIRLS.map((d) => <DogCard key={d.id} dog={d} galleries={galleries} onOpen={openGallery} />)}
            </div>
          </div>

          <div>
            <SubHeading>Available Studs</SubHeading>
            <div className="flex flex-wrap justify-center gap-6">
              {STUDS.map((d) => <DogCard key={d.id} dog={d} galleries={galleries} onOpen={openGallery} />)}
            </div>
          </div>
        </div>
      </section>


      <Ticker />

      {/* VIDEO */}
      <section id="video" className="py-24 px-6">
        <div className="max-w-5xl mx-auto">
          <SectionHeader label="See Our Frenchies In Action" title="Watch Our" highlight="Puppies Grow" />
          <GlassCard hover={false} className="p-3">
            <div className="relative h-[300px] sm:h-[450px] rounded-xl overflow-hidden bg-gradient-to-br from-[#1a1a1a] to-[#333] flex items-center justify-center">
              {videoPath ? (
                <video src={fileUrl(videoPath)} className="w-full h-full object-cover" autoPlay loop muted playsInline controls data-testid="puppy-video" />
              ) : (
                <>
                  <button className="relative kk-play-rings w-20 h-20 rounded-full bg-[#BB0000] flex items-center justify-center hover:bg-[#8B0000] transition-colors" data-testid="video-play">
                    <PlayCircle size={44} color="#fff" />
                  </button>
                  <span className="absolute bottom-4 right-5 text-[#777] text-xs">Upload your puppy video from the Admin panel</span>
                </>
              )}
            </div>
          </GlassCard>
          <div className="flex flex-col sm:flex-row gap-6 justify-center mt-7">
            <button onClick={() => navigate("/puppy-waitlist")} className="text-[#BB0000] font-display uppercase tracking-[0.12em] text-sm font-bold hover:text-[#8B0000]" data-testid="video-puppy-link">
              Join the Puppy Waitlist
            </button>
            <button onClick={() => navigate("/stud-waitlist")} className="text-[#A8A9AD] font-display uppercase tracking-[0.12em] text-sm font-bold hover:text-[#BB0000]" data-testid="video-stud-link">
              Stud Service Waitlist
            </button>
          </div>
        </div>
      </section>

      {/* HEALTH */}
      <section id="health" className="py-24 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="kk-label mb-3">Health & Genetics</p>
            <h2 className="kk-heading text-3xl sm:text-4xl lg:text-5xl mb-5">Fully tested,<br /><span className="text-[#BB0000]">no compromises</span></h2>
            <p className="text-[#E0E0E0] leading-relaxed mb-7">
              Every breeding pair undergoes comprehensive OFA and DNA testing before they ever join our program. We breed for health and temperament first, then for the rare colors and structure Frenchie lovers adore.
            </p>
            <div className="flex flex-wrap gap-3">
              {COLORS.map((c) => (
                <span key={c} className="px-4 py-2 rounded-full text-sm font-medium bg-[rgba(187,0,0,0.12)] border border-[rgba(187,0,0,0.3)] text-[#E0E0E0]">{c}</span>
              ))}
            </div>
          </div>
          <GlassCard hover={false} className="p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {HEALTH.map((h) => (
                <div key={h} className="flex items-center gap-3" data-testid={`health-${h.split(" ")[0].toLowerCase()}`}>
                  <span className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(76,175,80,0.15)" }}>
                    <CheckCircle2 size={20} color="#4CAF50" />
                  </span>
                  <span className="text-[#E0E0E0] text-sm">{h}</span>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 px-6">
        <div className="max-w-[680px] mx-auto">
          <SectionHeader label="FAQ" title="Common Questions" highlight="Answered" />
          <div className="space-y-4">
            {FAQS.map((f, i) => (
              <FaqItem key={i} idx={i} q={f.q} a={f.a} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? -1 : i)} />
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="py-24 px-6">
        <div className="max-w-[600px] mx-auto">
          <SectionHeader label="Get In Touch" title="Contact" highlight="Us" />
          <ContactForm />
        </div>
      </section>
    </Layout>
  );
}
